import React from 'react';
import moment from 'moment';
import Collapse from '@material-ui/core/Collapse';
import TimePicker from 'rc-time-picker';
import TimezoneSelect from 'react-timezone-select';
import countryList from 'country-list';
import SquareRadio from '../Controls/Buttons/SquareRadio/SquareRadio';
import CircleRadio from '../Controls/Buttons/CircleRadio/CircleRadio';
import PolicyField from './PolicyField';
import { convertCountriesDataToSelectOptions, getMatchingByOptions } from './PoliciesUtils';
import {
    DAYS_OPTIONS,
    FEATURE_FLAGS_NAMES,
    LOCATION_ENABLED_PROTOCOLS,
    LOCATION_TYPE_OPTIONS,
    MFA_WINDOW_TYPES,
    POLICY_ACTION_TYPES,
    REQUIRE_MFA_FROM_TYPES,
    RETROSPECTIVE_VALIDATION_OPTIONS,
    TIME_RESTRICTION_TYPE_OPTIONS,
    UNPAIRED_USERS_ACTION_OPTIONS
} from '@silverfort-nodelibs/common/utils/consts';
import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';

import styles from './CssModules/PolicyRowMainBody.module.css';
import fieldStyles from './CssModules/PolicyField.module.css';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import PolicySelectField from './components/PolicySelectField';
import PolicyAsyncCreatableSelectField from './components/PolicySelectField/PolicyAsyncCreatableSelectField';
import { AccordingToTooltip } from './components/AccordingToTooltip';

const PolicyRowAdvancedOptions = ({
    policyBodyData,
    policyConfiguration,
    setPolicyConfiguration,
    onFieldChanged,
    isMfaWindowEveryTime,
    setIsMfaWindowEveryTime,
    mfaWindowType,
    setMfaWindowType,
    mfaWindowValueByType,
    setMfaWindowValueByType,
    nextPolicyGroupId,
    setNextPolicyGroupId
}) => {
    const geoLocationEnabled = useFeature(FEATURE_FLAGS_NAMES.GeoLocationEnabled);
    const accordingToIpFF = useFeature(FEATURE_FLAGS_NAMES.AccordingToIp)
    const accordingToIpFFEnabled = isFeatureActive(accordingToIpFF);

    const onMfaWindowTypeChanged = (event) => {
        setMfaWindowType(event);
        if (mfaWindowValueByType !== 0) {
            updateMFAWindow(event * mfaWindowValueByType);
        }
    };

    const onMfaWindowValueByTypeChanged = (event) => {
        let newValue = event.target.value !== '' ? parseInt(event.target.value) : '';
        if (newValue < 0) {
            newValue = 0;
        }
        setMfaWindowValueByType(newValue);
        if (newValue && newValue !== 0) {
            updateMFAWindow(mfaWindowType * newValue);
        } else {
            onFieldChanged('mfa_window', 0);
        }
    };

    const onMfaWindowChange = (event) => {
        const newIsMfaWindowEveryTime = event.target.value === 'true';
        setIsMfaWindowEveryTime(newIsMfaWindowEveryTime);
        const newMfaWindowValue = newIsMfaWindowEveryTime ? 0 : mfaWindowValueByType * mfaWindowType;
        onFieldChanged('mfa_window', newMfaWindowValue);
    };

    const updateMFAWindow = (newValue) => {
        const newMfaWindowValue =
            newValue !== undefined && newValue !== null ? newValue : mfaWindowValueByType * mfaWindowType;
        onFieldChanged('mfa_window', newMfaWindowValue);
        setIsMfaWindowEveryTime(newValue === 0);
    };

    const onPolicyGroupChanged = (event) => {
        const newPolicyGroups = [];
        const newGroups = [];
        const eventGroups = event.map(({ value, label, ...eventGroup }) => ({ ...eventGroup, id: value, name: label }));
        for (const policyGroup of eventGroups) {
            if (policyGroup.company_id === undefined && !Number.isInteger(policyGroup.id)) {
                policyGroup.id = nextPolicyGroupId;
                setNextPolicyGroupId(nextPolicyGroupId - 1);
                newGroups.push(policyGroup);
            }
            newPolicyGroups.push(policyGroup);
        }
        setPolicyConfiguration({
            ...policyConfiguration,
            policiesGroups: [...policyConfiguration.policiesGroups, ...newGroups]
        });
        onFieldChanged('groups', newPolicyGroups);
    };

    const getFilteredGroups = (input) => {
        return Promise.resolve(
            policyConfiguration.policiesGroups
                .filter((policyGroup) => policyGroup.name.toLowerCase().includes(input.toLowerCase()))
                .map(({ id, name, ...policyGroupDetails }) => ({
                    ...policyGroupDetails,
                    value: id,
                    label: name
                }))
        );
    };

    const onTimeRestrictionFieldChanged = (newVal, fieldName) => {
        const newTimeRestrictionData = { ...policyBodyData.time_restriction_data, [fieldName]: newVal };
        onFieldChanged('time_restriction_data', newTimeRestrictionData);
    };

    const onLocationsListChanged = (fId, event) => {
        onFieldChanged(fId, event);
    };

    const retrospectiveOptions = Object.values(RETROSPECTIVE_VALIDATION_OPTIONS).filter((option) => {
        return (
            policyBodyData.action_type === POLICY_ACTION_TYPES.MFA.value ||
            option.value !== RETROSPECTIVE_VALIDATION_OPTIONS.CONDITIONAL_ENABLE.value
        );
    });

    const from_time =
        policyBodyData.time_restriction_data.from_time !== ''
            ? moment(policyBodyData.time_restriction_data.from_time).seconds(0)
            : null;

    const to_time =
        policyBodyData.time_restriction_data.to_time !== ''
            ? moment(policyBodyData.time_restriction_data.to_time).seconds(0)
            : null;

    let showTimeTooltip = false;
    if (from_time && to_time && from_time >= to_time) {
        showTimeTooltip = true;
    }

    return (
        <div className={styles.advancedOptions}>
            <div className={styles.advancedOptionsFieldContainer}>
                <PolicyField fieldTitle={'Policy restrictions'} isSectionContainer={true}>
                    <div className={fieldStyles.policyFieldsSection}>
                        <PolicyField fieldTitle={'Time of day'} fieldClass={fieldStyles.timeRestrictionField}>
                            <div className={fieldStyles.timeRestrictionSection}>
                                <PolicySelectField
                                    name="form-field-name"
                                    value={policyBodyData.time_restriction_type}
                                    onChange={(event) => {
                                        onFieldChanged('time_restriction_type', event);
                                    }}
                                    options={Object.values(TIME_RESTRICTION_TYPE_OPTIONS)}
                                />
                                {policyBodyData.time_restriction_type !==
                                    TIME_RESTRICTION_TYPE_OPTIONS.ALWAYS.value && (
                                        <div>
                                            <div className={styles.daysSelection}>
                                                <span className={styles.timeRestrictionLabel}>Days:</span>
                                                <SquareRadio
                                                    className={fieldStyles.policyFieldData}
                                                    handleSquareRadioChange={(newVal) => {
                                                        onTimeRestrictionFieldChanged(newVal, 'days');
                                                    }}
                                                    valuesList={Object.values(DAYS_OPTIONS).map((option) => {
                                                        return { label: option.shortName, value: option.value };
                                                    })}
                                                    radioValue={policyBodyData.time_restriction_data.days}
                                                    lightMode={true}
                                                    type={'checkbox'}
                                                />
                                            </div>
                                            <div className={styles.timeSelection}>
                                                <span className={styles.timeRestrictionLabel}>From:</span>
                                                <TimePicker
                                                    popupClassName={styles.timePicker}
                                                    id="fromTime"
                                                    allowEmpty={false}
                                                    minuteStep={15}
                                                    showSecond={false}
                                                    value={from_time}
                                                    onChange={(newVal) => {
                                                        onTimeRestrictionFieldChanged(
                                                            (newVal || '').format('YYYY-MM-DDTHH:mm:ss'),
                                                            'from_time'
                                                        );
                                                    }}
                                                />
                                                <span
                                                    className={`${styles.timeRestrictionLabel} ${styles.toTimeRestriction}`}>
                                                    <span className={styles.toTimeRestrictionLabel}>To:</span>
                                                </span>
                                                <TimePicker
                                                    popupClassName={styles.timePicker}
                                                    id="toTime"
                                                    allowEmpty={false}
                                                    minuteStep={15}
                                                    showSecond={false}
                                                    value={to_time}
                                                    onChange={(newVal) => {
                                                        onTimeRestrictionFieldChanged(
                                                            (newVal || '').format('YYYY-MM-DDTHH:mm:ss'),
                                                            'to_time'
                                                        );
                                                    }}
                                                />

                                                {showTimeTooltip && (
                                                    <span
                                                        className={`${styles.timeRestrictionLabel} ${styles.toTimeRestrictionWarning}`}>
                                                        On the following day
                                                    </span>
                                                )}
                                            </div>
                                            <div className={styles.timezoneSelection}>
                                                <span
                                                    className={`${styles.timeRestrictionLabel} ${styles.timezoneRestriction}`}>
                                                    Timezone:
                                                </span>
                                                <TimezoneSelect
                                                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                                    value={policyBodyData.time_restriction_data.timezone}
                                                    onChange={(newVal) => {
                                                        onTimeRestrictionFieldChanged(newVal, 'timezone');
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </PolicyField>

                        <Collapse
                            in={
                                isFeatureActive(geoLocationEnabled) &&
                                Object.keys(policyBodyData).some(
                                    (ruleKey) => LOCATION_ENABLED_PROTOCOLS.includes(ruleKey) && policyBodyData[ruleKey]
                                )
                            }>
                            <PolicyField
                                fieldTitle={'User location'}
                                fieldClass={fieldStyles.geolocationField}
                                tooltipText={
                                    <div>
                                        Silverfort will preform a country lookup on the source IP address to apply
                                        policy based on the country.<br></br> Note that access attempts from internal
                                        IPs (some applications don't report the user IP) won't have locations base
                                        restrictions enforced
                                    </div>
                                }>
                                <div className={fieldStyles.geolocationSection}>
                                    <PolicySelectField
                                        className={fieldStyles.policyFieldData}
                                        name="form-field-name"
                                        value={policyBodyData.location_type}
                                        onChange={(event) => {
                                            onFieldChanged('location_type', event);
                                        }}
                                        options={Object.values(LOCATION_TYPE_OPTIONS)}
                                    />
                                    {policyBodyData.location_type !== LOCATION_TYPE_OPTIONS.EVERYWHERE.value && (
                                        <div>
                                            <div className={styles.timezoneSelection}>
                                                <span className={styles.locationLabel}>Countries:</span>
                                                <PolicySelectField
                                                    multi
                                                    className={fieldStyles.multiSelectData}
                                                    name="form-field-name"
                                                    value={
                                                        (policyBodyData.location_type ===
                                                            LOCATION_TYPE_OPTIONS.INCLUDE_LOCATIONS.value
                                                            ? policyBodyData.locations
                                                            : policyBodyData.excluded_locations) || []
                                                    }
                                                    onChange={(event) =>
                                                        onLocationsListChanged(
                                                            policyBodyData.location_type ===
                                                                LOCATION_TYPE_OPTIONS.INCLUDE_LOCATIONS.value
                                                                ? 'locations'
                                                                : 'excluded_locations',
                                                            event
                                                        )
                                                    }
                                                    options={convertCountriesDataToSelectOptions(countryList.getData())}
                                                />
                                            </div>
                                            <div className={styles.enforceGeolocationRadioField}>
                                                <span className={styles.locationLabel}>
                                                    If Geo-location services are unavailable:
                                                </span>
                                                <CircleRadio
                                                    handleCircleRadioChange={(event) =>
                                                        onFieldChanged(
                                                            'enforce_location_if_na',
                                                            event.target.value === 'true'
                                                        )
                                                    }
                                                    valuesList={[
                                                        {
                                                            value: true,
                                                            label: 'Apply policy without Geo-location restrictions'
                                                        },
                                                        { value: false, label: 'Ignore this policy' }
                                                    ]}
                                                    radioValue={policyBodyData.enforce_location_if_na}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </PolicyField>
                        </Collapse>
                    </div>
                </PolicyField>

                <Collapse
                    in={
                        policyBodyData.action_type === POLICY_ACTION_TYPES.MFA.value ||
                        policyBodyData.action_type === POLICY_ACTION_TYPES.BRIDGE.value
                    }>
                    <PolicyField fieldTitle={'MFA frequency'} isSectionContainer={true}>
                        <div className={fieldStyles.policyFieldsSection}>
                            <PolicyField fieldTitle={'Require MFA'}>
                                <div className={`${fieldStyles.policyFieldData} ${fieldStyles.radioMfaWindowSection}`}>
                                    <CircleRadio
                                        handleCircleRadioChange={onMfaWindowChange}
                                        valuesList={[
                                            { value: true, label: 'Every time' },
                                            { value: false, label: 'Every:' }
                                        ]}
                                        radioValue={isMfaWindowEveryTime}
                                    />
                                </div>
                                <div
                                    className={`${fieldStyles.policyFieldData} ${fieldStyles.inputsMfaWindowSection} ${isMfaWindowEveryTime ? fieldStyles.disabledMfaWindowSection : ''
                                        }`}>
                                    <input
                                        className={fieldStyles.inputField}
                                        id={'mfaWindow' + policyBodyData.id}
                                        value={mfaWindowValueByType}
                                        type="number"
                                        min="0"
                                        onChange={onMfaWindowValueByTypeChanged}
                                        disabled={isMfaWindowEveryTime}
                                    />
                                    <div className={fieldStyles.mfaWindowTypeSelector}>
                                        <PolicySelectField
                                            name="form-field-name"
                                            value={mfaWindowType}
                                            options={Object.values(MFA_WINDOW_TYPES)}
                                            onChange={onMfaWindowTypeChanged}
                                            disabled={isMfaWindowEveryTime}
                                        />
                                    </div>
                                </div>
                            </PolicyField>
                            <PolicyField fieldTitle={'From'}>
                                <PolicySelectField
                                    name="form-field-name"
                                    value={policyBodyData.auto_renew}
                                    onChange={(event) => {
                                        onFieldChanged('auto_renew', event);
                                    }}
                                    options={Object.values(REQUIRE_MFA_FROM_TYPES)}
                                />
                            </PolicyField>
                            <PolicyField
                                fieldTitle={'According to'}
                                fieldClass={fieldStyles.multiChoiceField}
                                tooltipText={
                                    <AccordingToTooltip />
                                }>
                                <PolicySelectField
                                    multi
                                    placeholder="User Only"
                                    name="form-field-name"
                                    value={policyBodyData.matching_by}
                                    options={getMatchingByOptions(policyBodyData, accordingToIpFFEnabled)}
                                    onChange={(event) => {
                                        onFieldChanged('matching_by', event ? event : '');
                                    }}
                                />
                            </PolicyField>
                            <PolicyField
                                fieldTitle={'Policy grouping'}
                                fieldClass={fieldStyles.multiChoiceField}
                                tooltipText={`Group MFA policies together to reduce the number of MFA requests for a user, when accessing several different resources and devices. Each policy follows its own MFA frequency, but considers an MFA request for any policy within the group as the 'last' request for all policies in the group, for purposes of determining when next to request an MFA.`}>
                                <PolicyAsyncCreatableSelectField
                                    multi
                                    searchable
                                    placeholder={'Select or create'}
                                    name="form-field-name"
                                    value={policyBodyData.groups?.map(({ id, name, ...group }) => ({
                                        ...group,
                                        value: id,
                                        label: name
                                    }))}
                                    onChange={onPolicyGroupChanged}
                                    loadOptions={getFilteredGroups}
                                    formatCreateLabel={(name) => `Create Policy Group “${name}”`}
                                />
                            </PolicyField>
                        </div>
                    </PolicyField>
                </Collapse>
                <PolicyField
                    fieldTitle={'Retrospective validation'}
                    tooltipText={
                        'Configure when retrospective validation emails are sent to the users in the policy, confirming it was they who performed recent authentication actions.'
                    }>
                    <PolicySelectField
                        name="form-field-name"
                        value={policyBodyData.retrospective_validation}
                        onChange={(event) => {
                            onFieldChanged('retrospective_validation', event);
                        }}
                        options={retrospectiveOptions}
                    />
                </PolicyField>
                <Collapse
                    in={
                        policyBodyData.action_type === POLICY_ACTION_TYPES.MFA.value ||
                        policyBodyData.action_type === POLICY_ACTION_TYPES.BRIDGE.value
                    }>
                    <PolicyField
                        fieldTitle={'Unpaired user action'}
                        tooltipText={
                            'Action to be taken if the  selected MFA Tokens are not yet paired for the user.  Default setting is defined as a global setting.'
                        }>
                        <PolicySelectField
                            name="form-field-name"
                            value={policyBodyData.unpaired_users_action}
                            onChange={(event) => {
                                onFieldChanged('unpaired_users_action', parseInt(event));
                            }}
                            options={Object.values(UNPAIRED_USERS_ACTION_OPTIONS)}
                        />
                    </PolicyField>
                </Collapse>
            </div>
            <div className={styles.rightBodySection}></div>
        </div>
    );
};

export default PolicyRowAdvancedOptions;
